import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { getExperienceStrings, type StringKey } from '../lib/experience-strings';
import { detectSeason, getSeasonConfig, type JapanSeason, type SeasonConfig } from '../lib/seasons';

interface ExperienceCtx {
  /** Base theme slug — exp-manga, exp-ukiyo-e, or null (Standard). Mutually exclusive. */
  activeMode: string | null;
  /** Overlay slugs — combinable add-ons (exp-sensei, exp-seasons, exp-explorer). */
  activeOverlays: string[];
  getText: (key: StringKey) => string;
  season: JapanSeason;
  seasonConfig: SeasonConfig;
  isSensei: boolean;
  isSeasons: boolean;
  isManga: boolean;
  isExplorer: boolean;
  isUkiyo: boolean;
  isMangaV3: boolean;
  isMangaBW: boolean;
  toggleMangaBW: () => void;
  refresh: () => Promise<void>;
  tripSeason: JapanSeason | null;
  setTripDate: (date: string | null) => void;
}

const defaultSeason = detectSeason();

const Ctx = createContext<ExperienceCtx>({
  activeMode: null,
  activeOverlays: [],
  getText: (key) => getExperienceStrings(null)[key],
  season: defaultSeason,
  seasonConfig: getSeasonConfig(defaultSeason),
  isSensei: false,
  isSeasons: false,
  isManga: false,
  isExplorer: false,
  isUkiyo: false,
  isMangaV3: false,
  isMangaBW: false,
  toggleMangaBW: () => {},
  refresh: async () => {},
  tripSeason: null,
  setTripDate: () => {},
});

export function ExperienceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [activeMode, setActiveMode] = useState<string | null>(null);
  const [isMangaBW, setIsMangaBW] = useState<boolean>(() => {
    return localStorage.getItem('tg-manga-bw') === 'true';
  });

  const toggleMangaBW = useCallback(() => {
    setIsMangaBW(prev => {
      const next = !prev;
      if (next) localStorage.setItem('tg-manga-bw', 'true');
      else localStorage.removeItem('tg-manga-bw');
      return next;
    });
  }, []);
  const [activeOverlays, setActiveOverlays] = useState<string[]>([]);
  const [tripDate, setTripDate] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user) { setActiveMode(null); setActiveOverlays([]); return; }
    const { data } = await supabase
      .from('user_credits')
      .select('active_experience, active_overlays')
      .eq('user_id', user.id)
      .maybeSingle();
    setActiveMode(data?.active_experience || null);
    setActiveOverlays((data?.active_overlays as string[] | null) || []);
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const root = document.documentElement;
    if (activeMode === 'exp-manga') {
      root.setAttribute('data-manga', 'true');
    } else {
      root.removeAttribute('data-manga');
    }
    if (activeMode === 'exp-ukiyo-e') {
      root.setAttribute('data-ukiyo', 'true');
    } else {
      root.removeAttribute('data-ukiyo');
    }
    if (activeMode === 'exp-manga-v3') {
      root.setAttribute('data-manga-v3', 'true');
      // Repaint the --primary palette to manga red so every primary-* utility,
      // focus ring and toggle picks up the V3 accent.
      root.style.setProperty('--primary-50', '254 242 243');
      root.style.setProperty('--primary-100', '254 226 229');
      root.style.setProperty('--primary-200', '252 182 189');
      root.style.setProperty('--primary-300', '249 134 144');
      root.style.setProperty('--primary-400', '243 73 89');
      root.style.setProperty('--primary-500', '232 25 44');
      root.style.setProperty('--primary-600', '200 18 35');
      root.style.setProperty('--primary-700', '168 13 28');
      root.style.setProperty('--primary-800', '140 10 23');
      root.style.setProperty('--primary-900', '115 8 19');
    } else {
      root.removeAttribute('data-manga-v3');
      // Clear the overrides so the active theme's defaults take over again.
      root.style.removeProperty('--primary-50');
      for (let i = 100; i <= 900; i += 100) {
        root.style.removeProperty(`--primary-${i}`);
      }
    }
  }, [activeMode]);

  useEffect(() => {
    const root = document.documentElement;
    if (activeMode === 'exp-manga-v3' && isMangaBW) {
      root.setAttribute('data-manga-bw', 'true');
    } else {
      root.removeAttribute('data-manga-bw');
    }
  }, [activeMode, isMangaBW]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('trips')
      .select('start_date')
      .eq('user_id', user.id)
      .gte('start_date', new Date().toISOString().split('T')[0])
      .order('start_date', { ascending: true })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setTripDate(data.start_date);
      });
  }, [user]);

  // Base theme booleans
  const isManga = activeMode === 'exp-manga';
  const isUkiyo = activeMode === 'exp-ukiyo-e';
  const isMangaV3 = activeMode === 'exp-manga-v3';

  // Overlay booleans
  const isSensei = activeOverlays.includes('exp-sensei');
  const isSeasons = activeOverlays.includes('exp-seasons');
  const isExplorer = activeOverlays.includes('exp-explorer');

  // Experience strings still derive from the base theme — Manga/Ukiyo
  // re-skin user-facing copy. Overlays don't change copy.
  const strings = getExperienceStrings(activeMode);
  const getText = useCallback((key: StringKey) => strings[key], [strings]);

  const tripSeason = tripDate ? detectSeason(tripDate) : null;
  // Seasonal trip-date override now follows the overlay flag, not activeMode.
  const effectiveSeason = isSeasons && tripSeason ? tripSeason : detectSeason();
  const seasonConfig = getSeasonConfig(effectiveSeason);

  return (
    <Ctx.Provider value={{
      activeMode,
      activeOverlays,
      getText,
      season: effectiveSeason,
      seasonConfig,
      isSensei,
      isSeasons,
      isManga,
      isExplorer,
      isUkiyo,
      isMangaV3,
      isMangaBW,
      toggleMangaBW,
      refresh,
      tripSeason,
      setTripDate,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export const useExperience = () => useContext(Ctx);
